import React, { useEffect, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { Typography, Box, Breadcrumbs } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { DailyPayRun } from "./DailyPayroll";
import { PayrollCycleRun } from "./PayrollCycleRun";
import { PayrunCycleProvider } from "./PayrollCycleRun/PayrollCycleRunContext";
import { themes, theme } from "services/constants";
import { useSelector, useDispatch } from "react-redux";
import { FilterProvider } from "pages/FilterContext";
import { setFilterApi } from "services/Redux/userManagament";

// Payroll > Pay Processing is a sidebar flyout (see routes.js) and the only
// way to move between these screens; the old in-page tab list was removed.
// Each flyout item has its own URL under /admin/payroll/pay-processing/...,
// matching this folder's sub-folders, and opens the matching screen below.
export const PAY_PROCESSING_BASE_PATH = "/admin/payroll/pay-processing";

// URL segment -> screen key. The keys are also the backend sub_module_name
// values used to look up subModuleId, so they must not change.
const SEGMENT_TO_ITEM = {
  "daily-payroll": "Daily Payrun",
  "payroll-cycle-run": "Payroll Cycle Run",
};

// Names from the new Payroll menu, shown in the breadcrumb.
const ITEM_LABEL = {
  "Daily Payrun": "Daily Payroll",
  "Payroll Cycle Run": "Payroll Cycle Run",
};

const getPayProcessingSegment = (pathname) => {
  let path = pathname;
  while (path.endsWith("/")) path = path.slice(0, -1);
  if (!path.startsWith(`${PAY_PROCESSING_BASE_PATH}/`)) return "";
  return path
    .slice(PAY_PROCESSING_BASE_PATH.length + 1)
    .split("/")[0]
    .toLowerCase();
};

export const PayrollProcessing = () => {
  const { pathname } = useLocation();
  const segment = getPayProcessingSegment(pathname);
  const [selectPayrollProcessingItem, setSelectPayrollProcessingItem] =
    useState(SEGMENT_TO_ITEM[segment] || "Daily Payrun");
  const { domain, userScreenAccess, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const [isClickSchedule, setIsClickSchedule] = useState(false);
  const dispatch = useDispatch();

  const shiftModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Payroll Processing",
  );

  const subModuleId = shiftModule?.submodules?.find(
    (sub) => sub.sub_module_name === selectPayrollProcessingItem,
  )?.sub_module_id;

  // A flyout click changes the URL; open the matching screen.
  const currentItemRef = useRef(selectPayrollProcessingItem);
  currentItemRef.current = selectPayrollProcessingItem;
  useEffect(() => {
    const item = SEGMENT_TO_ITEM[segment];
    if (!item || item === currentItemRef.current) return;
    setSelectPayrollProcessingItem(item);
    setIsClickSchedule(false);
    dispatch(setFilterApi(true));
  }, [segment, dispatch]);

  const breadcrumbLabel =
    ITEM_LABEL[selectPayrollProcessingItem] ?? selectPayrollProcessingItem;

  const renderMainContent = () => {
    switch (selectPayrollProcessingItem) {
      case "Daily Payrun":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <DailyPayRun subModuleId={subModuleId} />
          </FilterProvider>
        );

      case "Payroll Cycle Run":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={null}
          >
            <PayrunCycleProvider>
              <PayrollCycleRun setIsClickSchedule={setIsClickSchedule} />
            </PayrunCycleProvider>
          </FilterProvider>
        );

      default:
        return <DailyPayRun subModuleId={subModuleId} />;
    }
  };

  // Incentive Payout and Advance moved to Pay Inputs; old links follow them.
  if (segment === "incentive-payout") {
    return <Redirect to="/admin/payroll/pay-inputs/incentive-payout" />;
  }
  if (segment === "advance") {
    return <Redirect to="/admin/payroll/pay-inputs/loans-and-advances" />;
  }

  // Old /admin/payroll/operations/processing links, the bare
  // /admin/payroll/pay-processing and unknown paths land on Daily Payroll.
  if (!SEGMENT_TO_ITEM[segment]) {
    return <Redirect to={`${PAY_PROCESSING_BASE_PATH}/daily-payroll`} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          height:
            selectPayrollProcessingItem === "Payroll Cycle Run"
              ? "115vh"
              : "94.5vh",
          backgroundColor: themes?.whiteColor,
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            minWidth: 0,
            backgroundColor: "#f5f5fd",
            transition: "all 0.3s ease",
          }}
        >
          {!isClickSchedule && (
            <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mb: 2 }}>
              <Typography>Payroll</Typography>
              <Typography>Pay Processing</Typography>
              <Typography color={themes?.blueColor}>
                {breadcrumbLabel}
              </Typography>
            </Breadcrumbs>
          )}

          {renderMainContent()}
        </Box>
      </Box>
    </ThemeProvider>
  );
};
